// src/pages/submit-app/SubmitAppPage.tsx
import React, { useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { submitApp } from '../../services/appRegistryService';
import { APP_CATEGORIES, APP_CATEGORY_LABELS, type AppCategory, type AppSubmission, type EnrichUrlResponse } from '../../types/appRegistry';
import { auth } from '../../config/firebase';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import MySubmissions from './MySubmissions';
import AllAppsAdmin from './AllAppsAdmin';

type FormState = {
  url: string;
  name: string;
  description: string;
  category: AppCategory;
  faviconUrl: string;
  screenshotUrl: string;
  iconColor: string;
};

const INITIAL_FORM: FormState = {
  url: '',
  name: '',
  description: '',
  category: 'productivity',
  faviconUrl: '',
  screenshotUrl: '',
  iconColor: '#3B82F6',
};

export default function SubmitAppPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [enriching, setEnriching] = useState(false);
  const [enrichError, setEnrichError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { isAdmin } = useIsAdmin();
  const [tab, setTab] = useState<'submit' | 'my-submissions' | 'all-apps'>('submit');

  const handleUrlBlur = useCallback(async () => {
    if (!form.url || enriching) return;
    try {
      new URL(form.url);
    } catch {
      setEnrichError('Please enter a valid URL');
      return;
    }

    setEnriching(true);
    setEnrichError(null);

    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch('/.netlify/functions/enrich-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ url: form.url }),
      });
      if (!response.ok) {
        throw new Error(`Enrichment request failed (${response.status})`);
      }
      const data: EnrichUrlResponse = await response.json();

      setForm((prev) => ({
        ...prev,
        name: data.name || prev.name,
        description: data.description || prev.description,
        faviconUrl: data.faviconUrl || prev.faviconUrl,
        screenshotUrl: data.screenshotUrl || prev.screenshotUrl,
        iconColor: data.suggestedColor || prev.iconColor,
      }));
    } catch (err) {
      console.error('Enrichment failed:', err);
      setEnrichError('Could not fetch app details. Please fill in manually.');
    } finally {
      setEnriching(false);
    }
  }, [form.url, enriching]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!user?.email || !form.name || !form.url) return;

      setSubmitting(true);
      try {
        const submission: AppSubmission = {
          name: form.name,
          description: form.description,
          url: form.url,
          category: form.category,
          faviconUrl: form.faviconUrl || null,
          screenshotUrl: form.screenshotUrl || null,
          iconColor: form.iconColor,
          customIconUrl: null,
        };
        await submitApp(submission, user.email);
        setSubmitted(true);
        setForm(INITIAL_FORM);
      } catch (err) {
        console.error('Submission failed:', err);
      } finally {
        setSubmitting(false);
      }
    },
    [form, user?.email]
  );

  const updateField = (field: keyof FormState, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-3xl mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Submit an App</h1>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={tab === 'submit' ? 'default' : 'outline'}
            onClick={() => { setTab('submit'); setSubmitted(false); }}
          >
            Submit New App
          </Button>
          <Button
            variant={tab === 'my-submissions' ? 'default' : 'outline'}
            onClick={() => setTab('my-submissions')}
          >
            My Submissions
          </Button>
          {isAdmin && (
            <Button
              variant={tab === 'all-apps' ? 'default' : 'outline'}
              onClick={() => setTab('all-apps')}
            >
              All Apps
            </Button>
          )}
        </div>

        {tab === 'all-apps' && isAdmin ? (
          <AllAppsAdmin />
        ) : tab === 'my-submissions' ? (
          <MySubmissions />
        ) : submitted ? (
          <Card>
            <CardContent className="py-12 text-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                App Submitted!
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Your app has been submitted for review. You can track its status in "My Submissions".
              </p>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => setSubmitted(false)}>Submit Another</Button>
                <Button variant="outline" onClick={() => setTab('my-submissions')}>
                  View My Submissions
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>App Details</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    App URL *
                  </label>
                  <input
                    type="url"
                    required
                    value={form.url}
                    onChange={(e) => updateField('url', e.target.value)}
                    onBlur={handleUrlBlur}
                    placeholder="https://your-app.com"
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  />
                  {enriching && (
                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                      Fetching app details...
                    </p>
                  )}
                  {enrichError && (
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">{enrichError}</p>
                  )}
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    App Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="My Awesome App"
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="What does this app do?"
                    rows={3}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category *
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => updateField('category', e.target.value as AppCategory)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  >
                    {APP_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {APP_CATEGORY_LABELS[cat]}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Icon Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Card Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={form.iconColor}
                      onChange={(e) => updateField('iconColor', e.target.value)}
                      className="h-10 w-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-400">{form.iconColor}</span>
                  </div>
                </div>

                {/* Preview: Favicon */}
                {form.faviconUrl && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Detected Icon
                    </label>
                    <img
                      src={form.faviconUrl}
                      alt="App favicon"
                      className="h-8 w-8 rounded"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {/* Preview: Screenshot */}
                {form.screenshotUrl && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Screenshot Preview
                    </label>
                    <img
                      src={form.screenshotUrl}
                      alt="App screenshot"
                      className="w-full max-w-md rounded border"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}

                <Button type="submit" disabled={submitting || !form.name || !form.url}>
                  {submitting ? 'Submitting...' : 'Submit App for Review'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
